const API_BASE = "/api/v1";

let csrfToken: string =
  typeof document === "undefined"
    ? ""
    : (document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content") ?? "");

export function updateCsrfToken(token: string): void {
  csrfToken = token;
  if (typeof document === "undefined") return;
  const meta = document.querySelector('meta[name="csrf-token"]');
  if (meta) meta.setAttribute("content", token);
}

export function resetCsrfToken(): void {
  csrfToken = "";
}

let onAuthError: (() => void) | null = null;

export function setAuthErrorHandler(handler: () => void): void {
  onAuthError = handler;
}

export class AuthError extends Error {
  override name = "AuthError" as const;
}

export class ApiError extends Error {
  override name = "ApiError" as const;
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`API error ${status}`);
  }
}

export function apiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return fallback;
  return error instanceof Error ? error.message : fallback;
}

export async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-CSRF-Token": csrfToken,
  };

  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: "same-origin",
    headers: { ...headers, ...options.headers },
  });

  if (response.status === 401) {
    onAuthError?.();
    throw new AuthError("Unauthorized");
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new ApiError(response.status, body);
  }

  if (response.status === 204) return {} as T;
  return (await response.json()) as T;
}
